/**
 * The compact data-quality line, and the way out to the detail page.
 *
 * A COUNT OF HINTS, NOT OF DEPOSITS
 * ---------------------------------
 * One tenancy can raise several counters: the backend appends its flags with
 * independent `if`s and then does `for flag in st.flags: quality[flag] += 1`
 * (`dashboard_deposit_service.py`), so a single row with an amount that lives
 * only in the legacy column AND carries no deposit type increments two of them.
 * The sum is therefore a number of findings, never a number of deposits — which
 * is why every string here says "Datenhinweise" and none says "Kautionen" or
 * "Fälle". Overstating how many records are affected would send someone looking
 * for rows that do not exist.
 *
 * The sum is deliberately the sum of the counters, not the number of distinct
 * kinds: it is then exactly the sum of the figures the detail page lists, so the
 * two surfaces reconcile instead of quietly disagreeing.
 *
 * ONLY THE PUBLISHED FINDING KEYS
 * -------------------------------
 * `data_quality` on the landlord side carries nine keys, of which four
 * (`units_total`, `units_with_deposit`, `units_without_deposit`,
 * `insurance_units`) are context, not problems. Iterating the object would
 * report the size of the portfolio as a defect, so both sides go through
 * `selectQualityFindings` against the published row whitelists.
 *
 * A FAILED SOURCE IS NOT A CLEAN ONE
 * ----------------------------------
 * When a request failed there is no count to state. That side reads "nicht
 * verfügbar"; reporting it as zero would turn an unanswered question into an
 * all-clear.
 */
import React from "react";
import { AlertTriangle, ArrowRight, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";

import { Skeleton } from "@/components/ui/skeleton";
import {
  LANDLORD_QUALITY_ROWS,
  TENANT_QUALITY_ROWS,
  selectQualityFindings,
} from "../../../pages/admin/deposits/depositsFilters";

/** Total counter hits on one side, or null when that side has no answer. */
function hintCount(dataQuality, rows, { unavailable }) {
  if (unavailable) return null;
  return selectQualityFindings(dataQuality, rows).reduce(
    (sum, row) => sum + row.count,
    0
  );
}

const hinweise = (n) => `${n} ${n === 1 ? "Hinweis" : "Hinweise"}`;

export default function DepositsTabQuality({
  tenantQuality,
  landlordQuality,
  tenantUnavailable = false,
  landlordUnavailable = false,
  loading = false,
}) {
  const tenantHints = hintCount(tenantQuality, TENANT_QUALITY_ROWS, {
    unavailable: tenantUnavailable,
  });
  const landlordHints = hintCount(landlordQuality, LANDLORD_QUALITY_ROWS, {
    unavailable: landlordUnavailable,
  });

  const total = (tenantHints || 0) + (landlordHints || 0);
  const bothAnswered = tenantHints !== null && landlordHints !== null;
  const clean = bothAnswered && total === 0;

  const parts = [];
  if (tenantHints === null) {
    parts.push("Mieterkautionen: nicht verfügbar");
  } else if (tenantHints > 0) {
    parts.push(`${hinweise(tenantHints)} Mieterkautionen`);
  }
  if (landlordHints === null) {
    parts.push("Vermieter-Sicherheiten: nicht verfügbar");
  } else if (landlordHints > 0) {
    parts.push(`${hinweise(landlordHints)} Vermieter-Sicherheiten`);
  }

  return (
    <div
      data-testid="deposits-tab-quality"
      aria-busy={loading || undefined}
      className="rounded-xl border border-border bg-card p-4 shadow-sm"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-[14px] font-semibold leading-tight text-card-foreground">
            Datenqualität
          </h2>

          {loading ? (
            <div className="mt-2">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="mt-1.5 h-3 w-64" />
            </div>
          ) : (
            <>
              <p
                data-testid="deposits-tab-quality-headline"
                className="mt-2 flex items-center gap-1.5 text-[13px] font-medium text-card-foreground"
              >
                {clean ? (
                  <CheckCircle2
                    aria-hidden
                    className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400"
                  />
                ) : (
                  <AlertTriangle
                    aria-hidden
                    className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400"
                  />
                )}
                {/* Never "X Kautionen" — see the header. */}
                {clean
                  ? "Keine offenen Datenhinweise"
                  : `${total} ${total === 1 ? "Datenhinweis" : "Datenhinweise"}`}
              </p>

              {parts.length > 0 ? (
                <p
                  data-testid="deposits-tab-quality-detail"
                  className="mt-1 text-[11px] leading-snug text-muted-foreground"
                >
                  {parts.join(" · ")}
                </p>
              ) : null}
            </>
          )}
        </div>

        <Link
          data-testid="deposits-tab-cta"
          to="/admin/kautionen"
          className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-[12px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
        >
          Kautionen &amp; Sicherheiten öffnen
          <ArrowRight aria-hidden className="h-3.5 w-3.5" />
        </Link>
      </div>

      <p className="mt-2 text-[11px] leading-snug text-muted-foreground">
        Tabellen, Filter und Datenqualität im Detail anzeigen
      </p>
    </div>
  );
}
